import { axiosApi } from "config";
import React, { createContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Loader } from "rsuite";
import { login } from "store/slices/auth";

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem("Riplestack_info");

        if (!token) {
          setChecking(false);
          return;
        }

        // Set token in axios headers
        axiosApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        // Only fetch user info if we don't have it
        if (!auth.user) {
          const res = await axiosApi.get("/user/info");
          const { platforms, token: newToken, user } = res.data;

          if (newToken) {
            localStorage.setItem("Riplestack_info", newToken);
            axiosApi.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${newToken}`;
          }

          dispatch(login({ platforms, token: newToken || token, user }));
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        localStorage.removeItem("Riplestack_info");
        delete axiosApi.defaults.headers.common["Authorization"];
      } finally {
        setChecking(false);
      }
    };

    initializeAuth();
  }, [auth.user, dispatch]);

  const authContent = useMemo(() => {
    if (checking) {
      return <Loader center content="Loading..." />;
    }

    const publicPaths = ["/", "/privacy", "/terms"];
    const authPaths = ["/auth/sign-in", "/auth/register"];
    const currentPath = window.location.pathname;

    // Always allow public routes
    if (publicPaths.includes(currentPath)) {
      return children;
    }

    // Allow auth routes if not authenticated
    if (authPaths.includes(currentPath) && !auth.user) {
      return children;
    }

    // If authenticated, allow access to protected routes and prevent access to auth routes
    if (auth.user) {
      if (authPaths.includes(currentPath)) {
        navigate("/main/default");
        return null;
      }
      return children;
    }

    // If we reach here, user is not authenticated and trying to access a protected route
    if (!authPaths.includes(currentPath)) {
      navigate("/auth/sign-in");
      return null;
    }

    return children;
  }, [checking, auth.user, children, navigate]);

  return (
    <AuthContext.Provider value={{ auth }}>{authContent}</AuthContext.Provider>
  );
};

export default AuthContext;
